import { Component, OnInit, inject, signal } from '@angular/core';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesApiService, TenderOpportunityRecord } from './sales-api.service';

type TenderViewMode = 'list' | 'pipeline';

const PIPELINE_STATUSES = ['Open', 'InProgress', 'Submitted', 'Won', 'Lost', 'Closed'] as const;

@Component({
  standalone: true,
  selector: 'ain-sales-tenders',
  imports: [AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, TableModule, ButtonModule],
  template: `
    <ain-page [title]="t('sales.tenders.title')" [description]="t('sales.tenders.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <div class="view-toggle">
              <button pButton type="button" [label]="t('sales.tenders.view.list')" [severity]="viewMode() === 'list' ? undefined : 'secondary'" (click)="setView('list')"></button>
              <button pButton type="button" [label]="t('sales.tenders.view.pipeline')" [severity]="viewMode() === 'pipeline' ? undefined : 'secondary'" (click)="setView('pipeline')"></button>
            </div>
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            @if (viewMode() === 'list') {
              <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            }
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          @if (viewMode() === 'list') {
            <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('sales.tenders.fields.tenderNumber') }}</th>
                  <th>{{ t('sales.tenders.fields.title') }}</th>
                  <th>{{ t('sales.tenders.fields.estimatedValue') }}</th>
                  <th>{{ t('sales.tenders.fields.dueDate') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.tenderNumber }}</button></td>
                  <td>{{ row.title }}</td>
                  <td>{{ row.estimatedValue | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.dueDateUtc ? (row.dueDateUtc | ainDate) : '-' }}</td>
                  <td>{{ statusLabel(row.status) }}</td>
                </tr>
              </ng-template>
            </p-table>
          } @else {
            <div class="pipeline">
              @for (status of pipelineStatuses; track status) {
                <div class="pipeline-col">
                  <header class="col-head">
                    <span>{{ statusLabel(status) }}</span>
                    <span class="count">{{ rowsForStatus(status).length }}</span>
                  </header>
                  @for (row of rowsForStatus(status); track row.id) {
                    <article class="pipeline-card" tabindex="0" (click)="openDetail(row.id)" (keyup.enter)="openDetail(row.id)">
                      <strong>{{ row.tenderNumber }}</strong>
                      <p>{{ row.title }}</p>
                      <p class="meta">{{ row.estimatedValue | ainAmount }} {{ row.currencyCode }}</p>
                      @if (row.dueDateUtc) {
                        <p class="meta">{{ row.dueDateUtc | ainDate }}</p>
                      }
                    </article>
                  }
                </div>
              }
            </div>
          }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel{display:grid;gap:10px}
    .toolbar{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
    .view-toggle{display:flex;gap:4px}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}
    .error{color:#b91c1c}
    .pipeline{display:grid;grid-template-columns:repeat(6,minmax(180px,1fr));gap:10px;overflow-x:auto;padding-bottom:8px}
    .pipeline-col{display:grid;gap:8px;align-content:start;background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:8px;min-height:120px}
    .col-head{display:flex;justify-content:space-between;align-items:center;font-size:12px;font-weight:700;color:#374151;margin-bottom:4px}
    .count{background:#e5e7eb;border-radius:999px;padding:2px 8px;font-size:11px}
    .pipeline-card{border:1px solid #e5e7eb;border-radius:8px;padding:8px;background:#fff;cursor:pointer;display:grid;gap:4px}
    .pipeline-card:hover{border-color:#93c5fd}
    .pipeline-card strong{font-size:13px;color:#1d4ed8}
    .pipeline-card p{margin:0;font-size:12px;color:#374151}
    .pipeline-card .meta{color:#6b7280;font-size:11px}
  `]
})
export class SalesTendersComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<TenderOpportunityRecord[]>([]);
  readonly selectedRows = signal<TenderOpportunityRecord[]>([]);
  readonly viewMode = signal<TenderViewMode>('list');
  readonly pipelineStatuses = PIPELINE_STATUSES;

  ngOnInit(): void { this.load(); }

  setView(mode: TenderViewMode): void { this.viewMode.set(mode); }

  load(): void {
    this.loading.set(true);
    this.api.listTenders().subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.tenders.errorLoad')); this.loading.set(false); }
    });
  }

  rowsForStatus(status: string): TenderOpportunityRecord[] {
    return this.rows().filter(row => row.status === status);
  }

  onSelectionChange(s: TenderOpportunityRecord[] | TenderOpportunityRecord | null): void {
    this.selectedRows.set(Array.isArray(s) ? s : s ? [s] : []);
  }

  openCreate(): void { void this.router.navigate(['/sales/tenders/new']); }
  openSelected(): void { const r = this.selectedRows()[0]; if (r) this.openDetail(r.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/tenders', id]); }

  statusLabel(status: string): string {
    const key = `sales.tenders.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  t(key: string): string { return this.localization.translate(key); }
}
